import { action } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { cached } from '@glimmer/tracking';
import {
  PrismComboSelectInput,
  PrismInput,
  PrismSelectInput,
} from '@mylighthouse/prism-ember';
import { task } from 'ember-concurrency';
import { t, tKey } from 'ember-intl';
import { not } from 'ember-truth-helpers';
import can from 'lumen/helpers/can';
import type {
  ParityManagerFiltersAssignee,
  ParityManagerFiltersMarketplace,
  ParityManagerFiltersRootCause,
  ParityManagerFiltersStatus,
  ParityManagerIssue,
} from 'lumen/models';
import type { IssueType, Source } from 'lumen/models/parity-manager/issue';
import {
  SOURCE_PRETTY_NAMES,
  SOURCES,
} from 'lumen/models/parity-manager/issue';
import type { Filters } from 'lumen/routes/market/parity/parity-manager';
import type AjaxService from 'lumen/services/ajax';
import type { HotelAndMarketServiceWithMarket } from 'lumen/services/hotel-and-market';
import type { SelectInputOptionWithId } from 'lumen/utils/constants/pi-constant-values';
import getAPIErrorMessage from 'lumen/utils/get-api-error-message';
import { timeout } from 'lumen/utils/waiters';

interface SourceOption {
  id: Source;
  label: string;
}

interface ParityParityManagerOtherInformationSignature {
  Args: {
    filters: Filters;
    selectedIssue?: ParityManagerIssue;
    selectedMetric: IssueType;
    updateEscalationLevelForSubscription: (subscriptionId: number) => void;
  };
}

export default class ParityParityManagerOtherInformation extends Component<ParityParityManagerOtherInformationSignature> {
  @service declare ajax: AjaxService;
  @service declare alerting: Services['alerting'];
  @service declare errorTracking: Services['error-tracking'];
  @service('hotel-and-market')
  declare hotelAndMarketService: HotelAndMarketServiceWithMarket;
  @service declare intl: Services['intl'];
  @service declare toast: Services['toast'];

  updateCommentTask = task(
    { restartable: true },
    async (event: Event): Promise<void> => {
      if (!this.args.selectedIssue) {
        return;
      }

      // Keep reference to the currently selected issue before the timeout (otherwise when switching issues, the wrong issue will be updated)
      const currentlySelectedIssue = this.args.selectedIssue;
      await timeout('extra-long');
      this.args.selectedIssue.comment = (
        event.target as HTMLInputElement
      ).value;
      void this.persistIssue(currentlySelectedIssue);
    },
  );

  updateRatePlanTask = task(
    { restartable: true },
    async (event: Event): Promise<void> => {
      if (!this.args.selectedIssue) {
        return;
      }

      // Keep reference to the currently selected issue before the timeout (otherwise when switching issues, the wrong issue will be updated)
      const currentlySelectedIssue = this.args.selectedIssue;
      await timeout('extra-long');
      this.args.selectedIssue.ratePlan = (
        event.target as HTMLInputElement
      ).value;
      void this.persistIssue(currentlySelectedIssue);
    },
  );

  @cached
  get assignees(): ParityManagerFiltersAssignee[] {
    return this.args.filters.assignees;
  }

  @cached
  get marketplaces(): ParityManagerFiltersMarketplace[] {
    return this.args.filters.marketplaces;
  }

  get rootCausePlaceholder():
    | 'parity_manager.rootcause.placeholder'
    | 'parity_manager.wholesaler.placeholder' {
    return this.args.selectedIssue?.isTestBookingIssue
      ? 'parity_manager.wholesaler.placeholder'
      : tKey('parity_manager.rootcause.placeholder');
  }

  @cached
  get rootcauses(): ParityManagerFiltersRootCause[] {
    return this.args.filters.rootcauses.filter(
      (option) => option.issueType === this.args.selectedIssue?.issueType,
    );
  }

  get rootCauseTitle():
    | 'parity_manager.issue.action.cause'
    | 'parity_manager.stats.wholesaler' {
    return this.args.selectedIssue?.isTestBookingIssue
      ? 'parity_manager.stats.wholesaler'
      : tKey('parity_manager.issue.action.cause');
  }

  get selectedAssignee(): ParityManagerFiltersAssignee | null | undefined {
    return this.args.selectedIssue?.assigneeId;
  }

  get selectedMarketplace():
    | ParityManagerFiltersMarketplace
    | null
    | undefined {
    return this.args.selectedIssue?.marketplace;
  }

  get selectedRootCause(): ParityManagerFiltersRootCause | null | undefined {
    return this.args.selectedIssue?.rootCauseId;
  }

  get selectedSource(): SourceOption | undefined {
    return this.sources.find(
      (source) => source.id === this.args.selectedIssue?.source,
    );
  }

  get selectedStatus(): ParityManagerFiltersStatus | null | undefined {
    return this.args.selectedIssue?.status;
  }

  @cached
  get sources(): SourceOption[] {
    return Object.values(SOURCES).map((source) => ({
      id: source,
      label: SOURCE_PRETTY_NAMES[source],
    }));
  }

  @cached
  get statuses(): ParityManagerFiltersStatus[] {
    return this.args.filters.statuses;
  }

  persistIssue(updatedIssue: ParityManagerIssue): Promise<void> {
    const serializedIssue = updatedIssue.serialize({ includeId: true }); // Camelcase to snakecase
    const postURL = `${this.ajax.host}/apigateway/v1/app/paritymanager/update?subscription_id=${updatedIssue.subscriptionId}&market_id=${this.hotelAndMarketService.market.id}`;

    return this.ajax
      .request(postURL, {
        method: 'POST',
        dataType: 'json',
        contentType: 'application/json; charset=utf-8',
        data: JSON.stringify({ issues: [serializedIssue] }),
      })
      .then(() => {
        this.toast.success(
          this.intl.t('parity_manager.update_success').toString(),
          {
            showIcon: true,
          },
        );
      })
      .catch((error) => {
        this.errorTracking.captureException(error);
        void this.alerting.showErrorAlert({
          text: getAPIErrorMessage(error, this.intl),
        });
      });
  }

  @action updateAssignee(
    dropdownChoice: ParityManagerFiltersAssignee | null,
  ): void {
    if (!this.args.selectedIssue) {
      return;
    }

    this.args.selectedIssue.assigneeId = dropdownChoice;
    void this.persistIssue(this.args.selectedIssue);
  }

  @action updateMarketplace(
    dropdownChoice: ParityManagerFiltersMarketplace | null,
  ): void {
    if (!this.args.selectedIssue) {
      return;
    }

    this.args.selectedIssue.marketplace = dropdownChoice;
    void this.persistIssue(this.args.selectedIssue);
  }

  @action updateRootCause(
    dropdownChoice: ParityManagerFiltersRootCause | null,
  ): void {
    if (!this.args.selectedIssue) {
      return;
    }

    this.args.selectedIssue.rootCauseId = dropdownChoice;
    void this.persistIssue(this.args.selectedIssue);
  }

  @action updateSource(
    dropdownChoice: SelectInputOptionWithId<Source> | null,
  ): void {
    if (!this.args.selectedIssue) {
      return;
    }

    if (!dropdownChoice) {
      this.args.selectedIssue.source = null;
    } else {
      this.args.selectedIssue.source = dropdownChoice.id;
    }
    void this.persistIssue(this.args.selectedIssue);
  }

  @action updateStatus(
    dropdownChoice: ParityManagerFiltersStatus | null,
  ): void {
    if (!this.args.selectedIssue) {
      return;
    }

    // We need to fetch the subscription ID before persisting the changes because this can fail due to a timing issue (issuesForExpandedIssueSummary is [] for a short period)
    const { subscriptionId } = this.args.selectedIssue;

    this.args.selectedIssue.status = dropdownChoice;
    void this.persistIssue(this.args.selectedIssue).then(() => {
      // If the status update was successful, we need to trigger an update of the escalation levels
      // The following action will trigger a request to get the updated escalation level for the passed subscription id (result will be pushed directly in the store)
      this.args.updateEscalationLevelForSubscription(subscriptionId);
    });
  }

  <template>
    <div class="other-information">
      <p class="text-200--bold mt-100">{{t
          "parity_manager.issue.action.assignee"
        }}</p>
      <PrismComboSelectInput
        @disabled={{not (can "market.paritymanager.issue.edit")}}
        @isSearchable={{true}}
        @isSecondary={{true}}
        @onChange={{this.updateAssignee}}
        @options={{this.assignees}}
        @placeholder={{t "parity_manager.assignee.placeholder"}}
        @unsafe_isClearable={{true}}
        @value={{this.selectedAssignee}}
      />

      <p class="text-200--bold">{{t "parity_manager.issue.action.status"}}</p>
      <PrismSelectInput
        @disabled={{not (can "market.paritymanager.issue.edit")}}
        @isSecondary={{true}}
        @onChange={{this.updateStatus}}
        @options={{this.statuses}}
        @placeholder={{t "general.dropdown.select_status"}}
        @unsafe_isClearable={{true}}
        @value={{this.selectedStatus}}
      />

      {{#if @selectedIssue.isTestBookingIssue}}
        <p class="text-200--bold">{{t "general.source"}}</p>
        <PrismSelectInput
          @disabled={{not (can "market.paritymanager.issue.edit")}}
          @isSecondary={{true}}
          @onChange={{this.updateSource}}
          @options={{this.sources}}
          @placeholder={{t "general.dropdown.select_source"}}
          @unsafe_isClearable={{true}}
          @value={{this.selectedSource}}
          data-test-id="dropdown-source"
        />

        <p class="text-200--bold">{{t "general.marketplace"}}</p>
        <PrismSelectInput
          @disabled={{not (can "market.paritymanager.issue.edit")}}
          @isSecondary={{true}}
          @onChange={{this.updateMarketplace}}
          @options={{this.marketplaces}}
          @placeholder={{t "general.dropdown.select_marketplace"}}
          @unsafe_isClearable={{true}}
          @value={{this.selectedMarketplace}}
          data-test-id="dropdown-marketplace"
        />
      {{/if}}

      <p class="text-200--bold">{{t this.rootCauseTitle}}</p>
      <PrismComboSelectInput
        @disabled={{not (can "market.paritymanager.issue.edit")}}
        @isSearchable={{true}}
        @isSecondary={{true}}
        @onChange={{this.updateRootCause}}
        @options={{this.rootcauses}}
        @placeholder={{t this.rootCausePlaceholder}}
        @unsafe_isClearable={{true}}
        @value={{this.selectedRootCause}}
      />

      {{#unless @selectedIssue.isBaitAndSwitchIssue}}
        <PrismInput
          @disabled={{not (can "market.paritymanager.issue.edit")}}
          @label="Rate code"
          @onInput={{this.updateRatePlanTask.perform}}
          @removePadding={{true}}
          @value={{@selectedIssue.ratePlan}}
        />
      {{/unless}}

      <PrismInput
        @disabled={{not (can "market.paritymanager.issue.edit")}}
        @label="Comment"
        @onInput={{this.updateCommentTask.perform}}
        @placeholder={{t "parity_manager.comment.placeholder"}}
        @removePadding={{true}}
        @textarea={{true}}
        @value={{@selectedIssue.comment}}
      />
    </div>
  </template>
}
